import { useState, useEffect, useRef } from 'react';
import { Table, Button, Select, message, Space, Tag, Drawer, Spin, Input, Popconfirm } from 'antd';
import { SendOutlined, CloseOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Option } = Select;
const { TextArea } = Input;

const Feedbacks = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [chatVisible, setChatVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchFeedbacks();
  }, [pagination.current, filterType, filterStatus]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getFeedbacks({
        page: pagination.current,
        page_size: pagination.pageSize,
        type: filterType,
        status: filterStatus,
      });
      setFeedbacks(res.data?.list || []);
      setPagination(prev => ({
        ...prev,
        total: res.data?.total || 0,
      }));
    } catch (error) {
      message.error('获取反馈列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (feedbackId) => {
    setMessagesLoading(true);
    try {
      const res = await adminAPI.getFeedbackMessages(feedbackId);
      setMessages(res.data || []);
    } catch (e) {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  // 打开聊天窗口
  const handleOpenChat = async (record) => {
    try {
      const res = await adminAPI.getFeedback(record.id);
      setCurrentFeedback(res.data);
      setChatVisible(true);
      setInputValue('');
      fetchMessages(record.id);
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  // 发送消息
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    setSending(true);
    try {
      await adminAPI.replyFeedback(currentFeedback.id, inputValue.trim());
      setInputValue('');
      // 刷新消息列表
      const res = await adminAPI.getFeedbackMessages(currentFeedback.id);
      if (res.data) {
        setMessages(res.data);
      }
      // 刷新列表
      fetchFeedbacks();
    } catch (error) {
      message.error('发送失败');
    } finally {
      setSending(false);
    }
  };

  // 关闭工单
  const handleClose = async () => {
    try {
      await adminAPI.closeFeedback(currentFeedback.id);
      message.success('工单已关闭');
      setChatVisible(false);
      fetchFeedbacks();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const getTypeName = (type) => {
    return type === 'contact' ? '商务合作' : '问题反馈';
  };

  const getTypeColor = (type) => {
    return type === 'contact' ? 'gold' : 'purple';
  };

  const getStatusTag = (status) => {
    const map = {
      pending: { color: 'orange', text: '待处理' },
      replied: { color: 'green', text: '已回复' },
      closed: { color: 'default', text: '已关闭' },
    };
    const item = map[status] || map.pending;
    return <Tag color={item.color}>{item.text}</Tag>;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left',
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (type) => <Tag color={getTypeColor(type)}>{getTypeName(type)}</Tag>,
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 80,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      render: (name) => name || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email) => email || '-',
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status) => getStatusTag(status),
    },
    {
      title: '提交时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (time) => time ? time.split('T')[0] + ' ' + time.split('T')[1]?.substring(0, 5) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleOpenChat(record)}>
            查看对话
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="fit-screen-page">
      {/* 功能区 */}
      <div className="page-header">
        <h2 className="page-title">工单反馈</h2>
      </div>

      {/* 搜索栏 */}
      <div className="search-bar" style={{ display: 'flex', gap: 10 }}>
        <Select
          placeholder="类型筛选"
          allowClear
          style={{ width: 120 }}
          onChange={(val) => { setFilterType(val || ''); setPagination(p => ({ ...p, current: 1 })); }}
        >
          <Option value="contact">商务合作</Option>
          <Option value="feedback">问题反馈</Option>
        </Select>
        <Select
          placeholder="状态筛选"
          allowClear
          style={{ width: 120 }}
          onChange={(val) => { setFilterStatus(val || ''); setPagination(p => ({ ...p, current: 1 })); }}
        >
          <Option value="pending">待处理</Option>
          <Option value="replied">已回复</Option>
          <Option value="closed">已关闭</Option>
        </Select>
      </div>

      {/* 表格容器 */}
      <div className="table-container">
        <Table
          columns={columns}
          dataSource={feedbacks}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page) => setPagination(p => ({ ...p, current: page })),
          }}
        />
      </div>

      {/* 聊天式对话窗口 */}
      <Drawer
        title={
          currentFeedback && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>工单 #{currentFeedback.id}</span>
              <Tag color={getTypeColor(currentFeedback.type)}>{getTypeName(currentFeedback.type)}</Tag>
              {getStatusTag(currentFeedback.status)}
            </div>
          )
        }
        placement="right"
        open={chatVisible}
        onClose={() => setChatVisible(false)}
        width={520}
        styles={{
          body: { display: 'flex', flexDirection: 'column', padding: 0 },
          header: { paddingRight: 48 },
        }}
        extra={
          currentFeedback && currentFeedback.status !== 'closed' && (
            <Popconfirm
              title="确定关闭此工单？"
              onConfirm={handleClose}
              okText="确定"
              cancelText="取消"
            >
              <Button size="small" danger icon={<CloseOutlined />}>
                关闭工单
              </Button>
            </Popconfirm>
          )
        }
      >
        {/* 用户信息区 */}
        {currentFeedback && (
          <div style={{ 
            padding: '12px 16px', 
            background: '#fafafa', 
            borderBottom: '1px solid #f0f0f0',
            fontSize: 12,
            color: '#666',
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
              <span>用户ID: <strong>{currentFeedback.user_id}</strong></span>
              {currentFeedback.name && <span>联系人: <strong>{currentFeedback.name}</strong></span>}
              {currentFeedback.email && <span>邮箱: <a href={`mailto:${currentFeedback.email}`}>{currentFeedback.email}</a></span>}
              {currentFeedback.phone && <span>电话: <strong>{currentFeedback.phone}</strong></span>}
            </div>
          </div>
        )}

        {/* 消息列表区域 */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: 16, 
          background: '#f5f5f5',
        }}>
          {messagesLoading ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <Spin />
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 50 }}>
              暂无消息记录
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  flexDirection: msg.sender_type === 'admin' ? 'row-reverse' : 'row',
                  marginBottom: 16,
                }}
              >
                {/* 头像 */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: msg.sender_type === 'admin'
                      ? 'linear-gradient(135deg, #52c41a 0%, #237804 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {msg.sender_type === 'admin' ? '我' : '用'}
                </div>
                {/* 消息内容 */}
                <div style={{ 
                  maxWidth: '70%', 
                  margin: msg.sender_type === 'admin' ? '0 12px 0 0' : '0 0 0 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.sender_type === 'admin' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>
                    {msg.sender_type === 'admin' ? '客服回复' : '用户消息'}
                    {' · '}
                    {msg.created_at?.replace('T', ' ').substring(5, 16)}
                  </div>
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: 12,
                      background: msg.sender_type === 'admin' ? '#52c41a' : '#fff',
                      color: msg.sender_type === 'admin' ? '#fff' : '#333',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      wordBreak: 'break-word',
                    }}
                  >
                    {msg.content}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 输入区域 */}
        {currentFeedback?.status === 'closed' ? (
          <div style={{ 
            padding: 16, 
            textAlign: 'center', 
            color: '#999', 
            background: '#fafafa',
            borderTop: '1px solid #f0f0f0',
          }}>
            工单已关闭
          </div>
        ) : (
          <div style={{ 
            padding: 12, 
            background: '#fff', 
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            gap: 8,
          }}>
            <TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="输入回复内容..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              style={{ flex: 1, borderRadius: 20, resize: 'none' }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              loading={sending}
              disabled={!inputValue.trim()}
              style={{ borderRadius: 20, height: 'auto' }}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Feedbacks;
